import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { useEventManagementService } from '../../services/eventManagementService';
import FieldMappingInput from './FieldMappingInput';

interface ChannelFieldMappingsProps {
  /** Required content keys for this channel (e.g. ['subject', 'description']) */
  keys: readonly string[];
  mappings: Record<string, string>;
  onChange: (mappings: Record<string, string>) => void;
  sampleEvent: Record<string, unknown>;
  customerId: string;
  /** Whether rule-level enrichment is on. Off: every key must be mapped. */
  summarize: boolean;
  /** Validation message per content key, already gated on a save attempt. */
  errors: Partial<Record<string, string>>;
  disabled?: boolean;
}

/**
 * Per-channel field mappings (C-02), driven by the rule's sample event. Each
 * channel declares its own content keys: ITSM uses {subject, description},
 * email uses {subject, body}. A filled mapping overrides the AI output for
 * that key; an empty one leaves it to the AI. With enrichment off there is no
 * AI output, so every key must be mapped.
 */
const ChannelFieldMappings: React.FC<ChannelFieldMappingsProps> = ({
  keys,
  mappings,
  onChange,
  sampleEvent,
  customerId,
  summarize,
  errors,
  disabled,
}) => {
  const eventService = useEventManagementService();
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);

  const hasSampleEvent = Object.keys(sampleEvent).length > 0;

  const handleChange = (key: string, value: string) => {
    const next = { ...mappings };
    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }
    onChange(next);
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const result = await eventService.suggestFieldMappings({
        customer_id: customerId,
        sample_event: sampleEvent,
        fields: [...keys],
      });
      onChange({ ...mappings, ...result.mappings });
      if (result.warnings.length > 0) {
        setSuggestNote(result.warnings.join('; '));
      }
    } catch (err) {
      setSuggestNote(err instanceof Error ? err.message : 'Failed to suggest mappings');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {keys.map((key) => (
        <FieldMappingInput
          key={key}
          label={key}
          value={mappings[key] || ''}
          onChange={(v) => handleChange(key, v)}
          sampleEvent={sampleEvent}
          multiline={key === 'body' || key === 'description'}
          disabled={disabled}
          emptyText={summarize ? 'Empty: uses AI' : 'Required'}
          error={errors[key]}
        />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="text"
          startIcon={suggesting ? <CircularProgress size={12} /> : <AutoFixHighIcon />}
          onClick={handleSuggest}
          disabled={disabled || suggesting || !hasSampleEvent}
        >
          {suggesting ? 'Suggesting...' : 'Suggest from sample event'}
        </Button>
        {!hasSampleEvent && (
          <Typography variant="caption" color="text.secondary">
            Add a sample event first
          </Typography>
        )}
        {suggestNote && (
          <Typography variant="caption" color="warning.main">
            {suggestNote}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChannelFieldMappings;
